import React, { memo, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { paper } from '@constants/mui-theme';
import { mapTabsConfig } from '@constants/component-configs';
import { getTrayDepth } from '@selectors/map-selector';
import VerticalTab from '@shared/vertical-tab';
import UnstyledHeading from '@components/shared/unstyled-heading';
import './tab-bar.scss';

const OPEN = 'rgba(0, 0, 0, 0.12) 0px 1px 6px, rgba(0, 0, 0, 0.12) 0px 1px 4px';
const CLOSE = 'rgba(0, 0, 0, 0.19) 0px 10px 30px, rgba(0, 0, 0, 0.23) 0px 6px 10px';
const MAP_NAVIGATION_HEADER_ID = 'map-tray-navigation-header';
const TabBar = () => {
  const depth = useSelector(getTrayDepth);
  const boxShadow = useMemo(() => depth > 0 ? OPEN : CLOSE, [depth]);
  const tabListRef = useRef(null);

  // Roving tabindex (WAI-ARIA Tabs pattern) - Up/Down move focus (and activate, matching
  // this tablist's existing click-to-activate behavior) between the vertical tabs.
  const handleTabListKeyDown = useCallback(event => {
    if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
      return;
    }
    const tabEls = Array.from(tabListRef.current.querySelectorAll('[role="tab"]'));
    const currentIndex = tabEls.indexOf(document.activeElement);
    if (currentIndex === -1) {
      return;
    }
    event.preventDefault();
    let nextIndex;
    if (event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + tabEls.length) % tabEls.length;
    } else if (event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % tabEls.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else {
      nextIndex = tabEls.length - 1;
    }
    tabEls[nextIndex].focus();
    tabEls[nextIndex].click();
  }, []);

  return (
    <div data-testid="map-tab-bar" styleName="map-tab-bar-container" style={{ ...paper, boxShadow }}>
      <UnstyledHeading rank={3} id={MAP_NAVIGATION_HEADER_ID} ariaOnly routeFocusSkip>Map Tray Tabs</UnstyledHeading>
      {/* role="tablist" only allows role="tab" children - the heading above provides the
          accessible name via aria-labelledby but must stay outside this element. */}
      {/* The tablist itself is never in the Tab order under the roving-tabindex pattern -
          only the selected tab is (see VerticalTab) - so it correctly has no tabIndex of
          its own despite handling keydown. */}
      {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
      <div
        ref={tabListRef}
        role="tablist"
        aria-labelledby={MAP_NAVIGATION_HEADER_ID}
        aria-orientation="vertical"
        onKeyDown={handleTabListKeyDown}
      >
        <VerticalTab {...mapTabsConfig.tabs.filters} />
        <VerticalTab {...mapTabsConfig.tabs.search} />
        <VerticalTab {...mapTabsConfig.tabs.details} />
        <VerticalTab {...mapTabsConfig.tabs.recent} />
      </div>
    </div>
  );
};

export default memo(TabBar);
